import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    Loader2, Megaphone, Users, BarChart3, Eye, Server,
    MessageSquare, Lock, ChevronDown, ChevronUp, Send,
} from 'lucide-react';
import { campaignsAPI } from '../../lib/api';
import api from '../../lib/api';
// P_USER_WELCOME_DISPATCH_2.1 — proper UTC→local conversion.
import { fmtLocalShort } from '../../utils/localTime';

/**
 * P_USER_WELCOME_DISPATCH_3.0b — Campaign Drawer.
 *
 * Read-only display for scheduled / sending / completed / cancelled
 * campaigns. Includes audit deep-link support: when `dispatchId` is
 * provided, the Message Preview defaults to the language actually
 * delivered to that recipient so support can answer "what exactly did
 * this employee see?" in one click.
 *
 * No engagement metrics computation here — everything is read from
 * the persisted snapshot.
 */

const STATUS_TONE = {
    draft:      'bg-muted text-muted-foreground border-border',
    scheduled:  'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900',
    sending:    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900',
    completed:  'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900',
    cancelled:  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900',
};

const Section = ({ icon: Icon, title, children, testid }) => (
    <Card className="p-4 space-y-2" data-testid={testid}>
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon className="w-4 h-4 text-muted-foreground" />
            {title}
        </div>
        <div className="text-sm text-foreground space-y-1">{children}</div>
    </Card>
);

const Row = ({ label, value, testid }) => (
    <div className="flex justify-between gap-3 text-sm" data-testid={testid}>
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground text-right">{value ?? '—'}</span>
    </div>
);

export const CampaignDrawer = ({ campaignId, dispatchId, onClose }) => {
    const { t, i18n } = useTranslation();
    const [campaign, setCampaign] = useState(null);
    const [dispatch, setDispatch] = useState(null);
    const [dispatchesAgg, setDispatchesAgg] = useState({ list: [], loading: true });
    const [channelBreakdown, setChannelBreakdown] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewLang, setPreviewLang] = useState('en');
    const [showFull, setShowFull] = useState(false);

    const reload = useCallback(async () => {
        if (!campaignId) return;
        setLoading(true);
        try {
            const c = await campaignsAPI.get(campaignId);
            setCampaign(c.data);
            if (dispatchId) {
                const d = await campaignsAPI.getDispatch(campaignId, dispatchId);
                setDispatch(d.data);
                setPreviewLang(d.data?.language_delivered || 'en');
            } else {
                setDispatch(null);
                setPreviewLang(c.data?.language_at_generation || 'en');
            }
            const dl = await campaignsAPI.listDispatches(campaignId, { limit: 2000 });
            setDispatchesAgg({ list: dl.data?.dispatches || [], loading: false });
            // P_DELIVERY_CHANNELS_5.0d — additive per-campaign channel breakdown.
            // Endpoint is super_admin-only; a 403 (or any failure) leaves the
            // section silent so non-super viewers see no regression.
            try {
                const cb = await api.get(`/communications/analytics/campaigns/${campaignId}/channels`);
                setChannelBreakdown(cb.data);
            } catch (_err) {
                setChannelBreakdown(null);
            }
        } catch (_e) {
            setCampaign(null); setDispatch(null);
            setDispatchesAgg({ list: [], loading: false });
        } finally { setLoading(false); }
    }, [campaignId, dispatchId]);

    useEffect(() => { reload(); }, [reload]);

    // Aggregated engagement from the per-row dispatch list.
    const engagement = useMemo(() => {
        const rows = dispatchesAgg.list;
        if (!rows.length) return null;
        let firstSeen = null, lastSeen = null, ackCount = 0, totalViews = 0;
        for (const r of rows) {
            if (r.first_seen_at && (!firstSeen || r.first_seen_at < firstSeen)) firstSeen = r.first_seen_at;
            if (r.last_seen_at  && (!lastSeen  || r.last_seen_at  > lastSeen )) lastSeen  = r.last_seen_at;
            if (r.acknowledged_at) ackCount += 1;
            totalViews += (r.view_count || 0);
        }
        return { firstSeen, lastSeen, ackCount, totalViews };
    }, [dispatchesAgg.list]);

    const open = !!campaignId;

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="campaign-drawer">
                <SheetHeader>
                    <SheetTitle data-testid="campaign-drawer-title">
                        {campaign?.title || t('campaignDrawer.loading')}
                    </SheetTitle>
                    <SheetDescription>
                        {dispatchId ? t('campaignDrawer.dispatchSubtitle') : t('campaignDrawer.subtitle')}
                    </SheetDescription>
                </SheetHeader>

                {loading ? (
                    <div className="p-8 text-center" data-testid="campaign-drawer-loading">
                        <Loader2 className="w-5 h-5 mx-auto animate-spin text-muted-foreground" />
                    </div>
                ) : !campaign ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="campaign-drawer-missing">
                        {t('campaignDrawer.missing')}
                    </div>
                ) : (
                    <div className="space-y-3 mt-4">
                        {/* Overview */}
                        <Section icon={Megaphone} title={t('campaignDrawer.overview')} testid="drawer-section-overview">
                            <Row label={t('campaignDrawer.fields.title')} value={campaign.title} testid="drawer-row-title" />
                            <Row label={t('campaignDrawer.fields.type')} value={t(`campaignBuilder.type.${campaign.type}`) || campaign.type} />
                            <Row label={t('campaignDrawer.fields.priority')} value={t(`campaignBuilder.priority.${campaign.priority}`) || campaign.priority} />
                            <Row label={t('campaignDrawer.fields.creator')} value={campaign.created_by_name || campaign.created_by} />
                            <Row label={t('campaignDrawer.fields.created')} value={fmtLocalShort(campaign.created_at, i18n.language) || '—'} />
                            <div className="flex justify-between gap-3 text-sm">
                                <span className="text-muted-foreground">{t('campaignDrawer.fields.status')}</span>
                                <Badge variant="outline" className={STATUS_TONE[campaign.status] || ''} data-testid="drawer-status">
                                    {t(`campaignsTable.status.${campaign.status}`)}
                                </Badge>
                            </div>
                            {campaign.scheduled_for ? (
                                <Row label={t('campaignDrawer.fields.scheduledFor')} value={fmtLocalShort(campaign.scheduled_for, i18n.language) || '—'} />
                            ) : null}
                        </Section>

                        {/* Recipients */}
                        <Section icon={Users} title={t('campaignDrawer.recipients')} testid="drawer-section-recipients">
                            <Row label={t('campaignDrawer.fields.organizations')}
                                value={(campaign.target_scope?.organization_ids || []).length} />
                            <Row label={t('campaignDrawer.fields.roles')}
                                value={(campaign.target_scope?.roles || []).join(', ') || '—'} />
                            <Row label={t('campaignDrawer.fields.users')}
                                value={(campaign.target_scope?.user_ids || []).length} />
                            <Row label={t('campaignDrawer.fields.recipientCount')}
                                value={campaign.metrics?.recipients_total ?? campaign.recipients_total ?? 0}
                                testid="drawer-recipient-count" />
                        </Section>

                        {/* Delivery Metrics */}
                        <Section icon={BarChart3} title={t('campaignDrawer.delivery')} testid="drawer-section-delivery">
                            <Row label={t('campaignsTable.cols.sent')} value={campaign.metrics?.delivered ?? 0} />
                            <Row label={t('campaignsTable.cols.seen')} value={campaign.metrics?.seen ?? 0} />
                            <Row label={t('campaignsTable.cols.ack')} value={campaign.metrics?.acknowledged ?? 0} />
                            <Row label={t('campaignsTable.cols.unopened')} value={campaign.metrics?.unopened ?? 0} />
                            <Row label={t('campaignDrawer.fields.failed')}
                                value={(campaign.metrics?.recipients_total ?? 0) - (campaign.metrics?.delivered ?? 0)} />
                        </Section>

                        {/* P_DELIVERY_CHANNELS_5.0d — Channel Breakdown (additive).
                            Reads delivery_attempts via /communications/analytics/campaigns/:id/channels.
                            Hidden if the endpoint returned nothing (e.g. non-super viewer). */}
                        {channelBreakdown ? (
                            <Section icon={Send} title={t('campaignDrawer.channelBreakdown', 'Channel Breakdown')}
                                testid="drawer-section-channels">
                                {[
                                    { key: 'in_app', label: t('campaignDrawer.channels.inApp', 'In-App') },
                                    { key: 'email',  label: t('campaignDrawer.channels.email',  'Email') },
                                    { key: 'sms',    label: t('campaignDrawer.channels.sms',    'SMS') },
                                    { key: 'push',   label: t('campaignDrawer.channels.push',   'Push') },
                                ].map(({ key, label }) => {
                                    const row = channelBreakdown.by_channel?.[key] || { delivered: 0, failed: 0, pending: 0, total: 0 };
                                    return (
                                        <div key={key} className="flex justify-between gap-3 text-sm" data-testid={`drawer-channel-${key}`}>
                                            <span className="text-muted-foreground">{label}</span>
                                            <span className="text-foreground text-right tabular-nums">
                                                <span data-testid={`drawer-channel-${key}-delivered`}>{row.delivered}</span>
                                                <span className="text-muted-foreground"> {t('campaignDrawer.channels.delivered', 'delivered')}</span>
                                                {row.failed > 0 ? (
                                                    <span className="ml-2 text-rose-600 dark:text-rose-400"
                                                        data-testid={`drawer-channel-${key}-failed`}>
                                                        · {row.failed} {t('campaignDrawer.channels.failed', 'failed')}
                                                    </span>
                                                ) : null}
                                                {row.pending > 0 ? (
                                                    <span className="ml-2 text-amber-600 dark:text-amber-400"
                                                        data-testid={`drawer-channel-${key}-pending`}>
                                                        · {row.pending} {t('campaignDrawer.channels.pending', 'pending')}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </div>
                                    );
                                })}
                                <div className="pt-1 mt-1 border-t border-border/60 flex justify-between text-xs text-muted-foreground"
                                    data-testid="drawer-channel-totals">
                                    <span>{t('campaignDrawer.channels.total', 'Total attempts')}</span>
                                    <span className="tabular-nums">{channelBreakdown.totals?.total ?? 0}</span>
                                </div>
                            </Section>
                        ) : null}

                        {/* Engagement */}
                        <Section icon={Eye} title={t('campaignDrawer.engagement')} testid="drawer-section-engagement">
                            <Row label={t('campaignDrawer.fields.firstSeen')}
                                value={fmtLocalShort(engagement?.firstSeen, i18n.language) || t('campaignDrawer.fields.notYet')} />
                            <Row label={t('campaignDrawer.fields.lastSeen')}
                                value={fmtLocalShort(engagement?.lastSeen, i18n.language) || t('campaignDrawer.fields.notYet')} />
                            <Row label={t('campaignDrawer.fields.ackCount')} value={engagement?.ackCount ?? 0} />
                            <Row label={t('campaignDrawer.fields.totalViews')} value={engagement?.totalViews ?? 0} />
                        </Section>

                        {/* Technical Info */}
                        <Section icon={Server} title={t('campaignDrawer.technical')} testid="drawer-section-technical">
                            <Row label={t('campaignDrawer.fields.campaignId')}
                                value={<code className="font-mono text-[11px]">{campaign.id}</code>} />
                            <Row label={t('campaignDrawer.fields.dispatchCount')}
                                value={dispatchesAgg.list.length} />
                            <Row label={t('campaignDrawer.fields.engine')}
                                value={dispatchesAgg.list[0]?.engine ?? '—'} />
                            <Row label={t('campaignDrawer.fields.translationModel')}
                                value={dispatchesAgg.list[0]?.translation_model ?? '—'} />
                            {campaign.send_started_at ? (
                                <Row label={t('campaignDrawer.fields.sendStarted')} value={fmtLocalShort(campaign.send_started_at, i18n.language) || '—'} />
                            ) : null}
                            {campaign.send_completed_at ? (
                                <Row label={t('campaignDrawer.fields.sendCompleted')} value={fmtLocalShort(campaign.send_completed_at, i18n.language) || '—'} />
                            ) : null}
                        </Section>

                        {/* Message preview */}
                        <Section icon={MessageSquare} title={t('campaignDrawer.preview')} testid="drawer-section-preview">
                            <div className="flex gap-2 mb-2" data-testid="drawer-lang-tabs">
                                {['en', 'fr', 'es'].map(l => (
                                    <button key={l} onClick={() => setPreviewLang(l)}
                                        className={`px-2.5 py-1 text-xs rounded-full border transition-colors
                                            ${previewLang === l
                                                ? 'bg-primary text-primary-foreground border-primary'
                                                : 'bg-background text-foreground border-input hover:bg-muted/60'}`}
                                        data-testid={`drawer-lang-${l}`}>
                                        {t(`campaignDrawer.lang.${l}`)}
                                    </button>
                                ))}
                            </div>
                            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground whitespace-pre-wrap" data-testid="drawer-preview-body">
                                {dispatch
                                    ? (dispatch[`body_${previewLang}`] || dispatch.body_en || '—')
                                    : (campaign[`body_${previewLang}`] || campaign.body_en || '—')}
                            </div>
                            {dispatchId ? (
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    {t('campaignDrawer.fields.languageDelivered')}: {(dispatch?.language_delivered || '').toUpperCase()}
                                </p>
                            ) : null}
                            <Button variant="outline" size="sm" onClick={() => setShowFull(v => !v)}
                                data-testid="drawer-toggle-full" className="mt-2">
                                {showFull ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                                {showFull ? t('campaignDrawer.hideFull') : t('campaignDrawer.viewFull')}
                            </Button>
                            {showFull ? (
                                <div className="space-y-2 mt-2" data-testid="drawer-full-bodies">
                                    {['en', 'fr', 'es'].map(l => (
                                        <div key={l} className="text-xs">
                                            <div className="uppercase text-muted-foreground mb-1">{t(`campaignDrawer.lang.${l}`)}</div>
                                            <div className="rounded-md border border-border bg-background p-2 whitespace-pre-wrap" data-testid={`drawer-full-body-${l}`}>
                                                {dispatch
                                                    ? (dispatch[`body_${l}`] || '—')
                                                    : (campaign[`body_${l}`] || '—')}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </Section>

                        {/* Immutability note */}
                        <Card className="p-3 border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20 flex items-start gap-2" data-testid="drawer-immutability-note">
                            <Lock className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                            <span className="text-xs text-foreground">{t('campaignDrawer.immutabilityNote')}</span>
                        </Card>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CampaignDrawer;
